import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { LEGAL } from "../lib/legal";
import { Logo } from "./Logo";

const LegalLayout = ({ children }) => {
  const { lang } = useLang();
  const c = LEGAL[lang]?.common || LEGAL.de.common;
  return (
    <main className="relative bg-stein-100 text-stein-900 min-h-screen">
      <header className="border-b border-stein-300 bg-stein-50">
        <div className="w-full max-w-[1100px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <Logo />
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[11px] sm:text-xs tracking-[0.24em] sm:tracking-[0.28em] uppercase text-stein-700 hover:text-stein-900 transition-colors duration-500 font-medium shrink-0"
          >
            <ArrowLeft size={14} />
            <span className="hidden sm:inline">{c.back_home}</span>
          </Link>
        </div>
      </header>
      <div className="w-full max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-24">
        {children}
      </div>
    </main>
  );
};

export const PrivacyPage = () => {
  const { lang } = useLang();
  const d = LEGAL[lang]?.privacy || LEGAL.de.privacy;
  return (
    <LegalLayout>
      <h1
        data-testid="privacy-title"
        className="font-heading text-3xl md:text-5xl font-normal tracking-[-0.01em] leading-[1.1]"
      >
        {d.title}
      </h1>
      <p className="mt-3 text-xs tracking-[0.28em] uppercase text-stein-700 font-medium">
        {d.updated}
      </p>
      <p className="mt-8 text-base md:text-lg text-stein-700 leading-[1.75]">
        {d.intro}
      </p>
      <div className="mt-12 space-y-10">
        {d.sections.map((s) => (
          <section key={s.h}>
            <h2 className="font-heading text-stein-900 text-xl md:text-2xl font-medium tracking-tight">
              {s.h}
            </h2>
            <p className="mt-3 text-[15px] md:text-base text-stein-700 leading-[1.75] whitespace-pre-line">
              {s.p}
            </p>
          </section>
        ))}
      </div>
    </LegalLayout>
  );
};

export const ImprintPage = () => {
  const { lang } = useLang();
  const d = LEGAL[lang]?.imprint || LEGAL.de.imprint;
  return (
    <LegalLayout>
      <h1
        data-testid="imprint-title"
        className="font-heading text-3xl md:text-5xl font-normal tracking-[-0.01em] leading-[1.1]"
      >
        {d.title}
      </h1>
      <div className="mt-10 space-y-8">
        {d.body.map((s) => (
          <section key={s.h}>
            <h2 className="text-xs tracking-[0.28em] uppercase text-stein-500 font-medium mb-2">
              {s.h}
            </h2>
            <p className="text-[15px] md:text-base text-stein-700 leading-[1.75] whitespace-pre-line">
              {s.p}
            </p>
          </section>
        ))}
      </div>
    </LegalLayout>
  );
};

export const NotFoundPage = () => {
  const { lang } = useLang();
  const c = LEGAL[lang]?.common || LEGAL.de.common;
  return (
    <LegalLayout>
      <div className="py-16 md:py-24 text-center">
        <div className="text-xs tracking-[0.32em] uppercase text-stein-500 font-medium">
          404
        </div>
        <div className="mt-4 h-px w-16 bg-chemara-violet/70 mx-auto" />
        <h1
          data-testid="not-found-title"
          className="mt-8 font-heading text-stein-900 text-4xl md:text-6xl font-normal tracking-[-0.01em] leading-[1.05]"
        >
          {c.not_found_title}
        </h1>
        <p className="mt-6 text-base md:text-lg text-stein-700 max-w-md mx-auto leading-[1.7]">
          {c.not_found_body}
        </p>
        <Link
          to="/"
          data-testid="not-found-back-home"
          className="mt-10 inline-flex items-center gap-3 px-6 py-4 bg-chemara-violet text-stein-900 rounded-md text-xs tracking-[0.28em] uppercase font-medium hover:bg-chemara-violet-deep transition-colors duration-500"
        >
          <ArrowLeft size={14} />
          {c.back_home}
        </Link>
      </div>
    </LegalLayout>
  );
};
